/* eslint-disable no-underscore-dangle */
import { useCallback, useEffect, useState, type ComponentType } from 'react';
import ReactFlow, {
  ReactFlowProvider,
  addEdge,
  MiniMap,
  useNodesState,
  useEdgesState,
  Node,
  Edge,
  Connection,
  Position,
  NodeProps,
} from 'react-flow-renderer';
import { Box } from '@chakra-ui/react';
import dagre from 'dagre'; // Assuming you are using React Flow types

import { accent, neutral } from 'styles/revampTokens';

import MapIndicatorNode from '../MapIndicatorNode';
import MapControls from '../MapControls';

import styles from './styles'; // Adjust according to your theme setup

const dagreGraph = new dagre.graphlib.Graph();
dagreGraph.setDefaultEdgeLabel(() => ({}));

// Matches MapIndicatorNode's actual card size (160px wide, ~72px tall with a
// two-line label) — dagre needs the real footprint to space nodes apart,
// the old 100x35 estimate was undersized and let cards overlap.
const nodeWidth = 160;
const nodeHeight = 72;

// Define types for nodes and edges, adjust according to your data model
interface LayoutedElements {
  nodes: Node[];
  edges: Edge[];
  layoutDirection: string;
}

const getLayoutedElements = (
  nodes: Node[],
  edges: Edge[],
  direction: string = 'LR',
): LayoutedElements => {
  const isHorizontal = direction === 'LR';
  dagreGraph.setGraph({ rankdir: direction, nodesep: 24, ranksep: 56 });

  const layoutDirection = dagreGraph?.graph()?.rankdir ?? 'LR';

  nodes.forEach((node: Node) => {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });

  edges.forEach((edge: Edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  nodes.forEach((node: Node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    node.targetPosition = isHorizontal ? Position.Left : Position.Top;
    node.sourcePosition = isHorizontal ? Position.Right : Position.Bottom;

    // We are shifting the dagre node position (anchor=center center) to the top left
    // so it matches the React Flow node anchor point (top left).
    node.position = {
      x: nodeWithPosition.x + nodeWidth / 2,
      y: nodeWithPosition.y + nodeHeight,
    };

    return node;
  });

  return { nodes, edges, layoutDirection };
};

// Define a custom node type for your React Flow nodes
const getNodeTypes = (indicatorNodeComponent?: ComponentType<NodeProps>) => ({
  indicator: indicatorNodeComponent ?? MapIndicatorNode,
});

// Every node on this map is type 'indicator' — the minimap outline is a
// single accent colour rather than a type-based switch.
const nodeStrokeColor = (): string => accent.solid;

export interface Properties {
  initialNodes: Node[];
  initialEdges: Edge[];
  indicatorNodeComponent?: ComponentType<NodeProps>;
}

const Map = ({ initialNodes, initialEdges, indicatorNodeComponent }: Properties) => {
  const [isDraggable, setIsDraggable] = useState(true);
  const [layoutDirection, setLayoutDirection] = useState<string>('LR');
  const [nodes, setNodes, onNodesChange] = useNodesState<Node[]>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge[]>([]);
  const nodeTypes = getNodeTypes(indicatorNodeComponent);

  useEffect(() => {
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
      initialNodes,
      initialEdges,
    );
    setNodes(layoutedNodes);
    setEdges(layoutedEdges);
  }, [initialEdges, initialNodes, setEdges, setNodes]);

  const onConnect = useCallback(
    (parameters: Connection) =>
      setEdges((eds) => addEdge({ ...parameters, type: 'smoothstep', animated: false }, eds)),
    [setEdges],
  );

  const onLayout = useCallback(
    (direction: string) => {
      const {
        nodes: layoutedNodes,
        edges: layoutedEdges,
        layoutDirection: layoutdirection,
      } = getLayoutedElements(nodes, edges, direction);

      setNodes([...layoutedNodes]);
      setEdges([...layoutedEdges]);
      setLayoutDirection(layoutdirection);
    },
    [nodes, edges, setNodes, setEdges],
  );

  return (
    <ReactFlowProvider>
      <Box sx={styles.mapContainer}>
        <ReactFlow
          style={{ position: 'relative', height: '100%' }}
          // The Figma map view draws every connector as the same thin
          // neutral line with no arrowhead (75644:18480 etc. — a plain dot,
          // 26px line, dot). The old per-column CHART_COLORS border and the
          // default arrow marker both fell out in favour of that flatter,
          // more minimal connector style.
          nodes={nodes}
          edges={edges.map((edge) => ({
            ...edge,
            markerEnd: undefined,
            style: { stroke: neutral.borderStrong, strokeWidth: 1.5 },
          }))}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          nodesDraggable={isDraggable}
          nodeTypes={nodeTypes}
          defaultZoom={1.5}
        />
        <MiniMap
          nodeStrokeColor={nodeStrokeColor}
          nodeStrokeWidth={4}
          maskColor='rgba(18, 24, 38, 0.2)'
          nodeBorderRadius={4}
          nodeColor='white'
        />
        <MapControls
          layoutDirection={layoutDirection}
          onLayout={onLayout}
          isDraggable={isDraggable}
          setIsDraggable={setIsDraggable}
        />
      </Box>
    </ReactFlowProvider>
  );
};

export default Map;
